import { LineString, Geometry as Geometry } from 'ol/geom';
import { makeAPICall } from 'models/api';
import {
    API_URL,
    DATA_PROJECTION,
    GEOSERVER_URL,
    MAP_PROJECTION,
} from 'config';
import { Feature } from 'ol';
import { GeoJSON as olGeoJson } from 'ol/format';
import {
    FeatureCollection,
    GeoJsonProperties,
    Geometry as geoJsonGeometry,
} from 'geojson';

export declare namespace API {
    export interface FireTrail {
        id: number;
        global_id: string;
        fire_trail_id: string;
        fire_trail_segment_id: string;
        topo_id: string;
        fire_trail_name: string;
        road_name_base: string;
        relevance: number;
        classification: string;
        vehicle_carrying_capacity: string;
        // works_type: string;
        note: string;
        source: string;
        lead_agency: string;
        bfmc_id: number;
        plan_id: string;
        tenure?: string;
        status?: string;
        source_fire_trail_segment_id?: string;
        source_fire_trail_id?: string;
        designated?: string;
    }

    export interface FireTrailAuditRecord {
        id: number;
        plan_id: string;
        feature_global_id: string;
        username: string;
        change_type: string;
        changed_on: string;
        reason_for_change: string;
    }

    export interface AggregateFireTrail {
        plan_id: string;
        bfmc_id: string;
        length: number;
        fire_trail_id: string;
        fire_trails: FireTrail[];
    }

    export interface DataUpload {
        id: number;
        upload_id: string;
        filename: string;
        upload_type: string;
        uploaded_by: string;
        bfmc_id: number;
        status: string;
        error_msg: string;
        uploaded_by_user: string;
    }

    export interface Extent {
        extent: number[];
    }
}

export declare namespace GeoServer {
    export interface FireTrailProperties {
        Id: number;
        GlobalId: string;
        FAFTId: string;
        FAFTSegmentId: string;
        TopoId?: string;
        FireTrailName?: string;
        RoadNameBase?: string;
        Relevance?: string;
        Classification?: string;
        VehicleCarryingCapacity?: string;
        // WorksType?: string;
        Note?: string;
        Source?: string;
        LeadAgency?: string;
        BfmcId: number;
        PlanId: string;
    }
}

export interface ActionResponse {
    success: boolean;
    error?: string;
}

export interface FireTrail {
    id: number;
    globalId: string;
    fireTrailId: string;
    fireTrailSegmentId: string;
    topoId?: string;
    fireTrailName?: string;
    roadNameBase?: string;
    relevance?: number;
    classification?: string;
    vehicleCarryingCapacity?: string;
    // worksType?: string;
    note?: string;
    source?: string;
    leadAgency?: string;
    geometry?: LineString;
    bfmcId: number;
    planId: string;
    tenure?: string;
    status?: string;
    sourceFireTrailId?: string;
    sourceFireTrailSegmentId?: string;
    designated?: string;
}

export interface FireTrailAuditRecord {
    id: number;
    planId: string;
    featureGlobalId: string;
    username: string;
    changeType: string;
    changedOn: Date;
    reasonForChange: string;
}

export interface Extent {
    extent: number[];
}

export interface AggregateFireTrail {
    planId: string;
    bfmcId: string;
    length: number;
    fireTrailId: string;
    fireTrails: FireTrail[];
}

export interface FireTrailLineFilters {
    skip?: number;
    limit?: number;
    planId?: string;
    featureID?: number;
}

export interface FireTrailAuditFields {
    planId?: string;
    globalFeatureID?: string;
}

export interface FireTrailAdd {
    payload: FeatureCollection<geoJsonGeometry, GeoJsonProperties>;
}

export interface FireTrailUpdate {
    id: number;
    payload: FeatureCollection<geoJsonGeometry, GeoJsonProperties>;
    reason_for_change: string;
}

export interface FireTrailBulkUpdate {
    fireTrailId: string;
    planId: string;
    tenure?: string;
    status?: string;
    classification?: string;
    vehicleCarryingCapacity?: string;
    designated?: string;
    reason_for_change: string;
}

export interface FireTrailDataImport {
    import_type: string;
    copy_from_service_type?: string;
    copy_from_service_url?: string;
    copy_from_service_id?: number;
    copy_from_layer_id?: string;
    copy_by_attribute_field?: string;
    copy_by_attribute_value?: string;
    bfmc_id: number;
    plan_id: string;
    fire_trail_id?: string;
    fire_trail_segment_id?: string;
    fire_trail_name?: string;
    topo_id?: string;
    road_name_base?: string;
    relevance?: number;
    classification?: string;
    vehicle_carrying_capacity?: string;
    // works_type?: string;
    note?: string;
    source?: string;
    lead_agency?: string;
    tenure?: string;
    status?: string;
    source_fire_trail_id?: string;
    source_fire_trail_segment_id?: string;
}

export interface FireTrailUpload {
    bfmcId: number;
    planId: string;
    uploadType: 'APPEND' | 'REPLACE';
    dataFile: File;
    reasonForChange: string;
}

export interface GetFireTrailUploads {
    bfmcId: number;
    planId: string;
}

const FIRE_TRAIL_ROUTE = `${API_URL}/fire-trails`;

export const mapAPIToFireTrail = ({
    global_id,
    plan_id,
    fire_trail_id,
    fire_trail_segment_id,
    topo_id,
    fire_trail_name,
    road_name_base,
    classification,
    vehicle_carrying_capacity,
    // works_type,
    note,
    source,
    lead_agency,
    bfmc_id,
    tenure,
    status,
    source_fire_trail_id,
    source_fire_trail_segment_id,
    designated,
    ...rest
}: API.FireTrail): FireTrail => ({
    ...rest,
    globalId: global_id,
    fireTrailId: fire_trail_id,
    fireTrailSegmentId: fire_trail_segment_id,
    topoId: topo_id,
    fireTrailName: fire_trail_name,
    roadNameBase: road_name_base,
    classification: classification,
    vehicleCarryingCapacity: vehicle_carrying_capacity,
    // worksType: works_type,
    note: note,
    tenure: tenure,
    status: status,
    source: source,
    leadAgency: lead_agency,
    bfmcId: bfmc_id,
    planId: plan_id,
    sourceFireTrailId: source_fire_trail_id,
    sourceFireTrailSegmentId: source_fire_trail_segment_id,
    designated: designated,
});

export const mapAPIToAggregateFireTrail = ({
    plan_id,
    bfmc_id,
    length,
    fire_trails,
    fire_trail_id,
    ...rest
}: API.AggregateFireTrail): AggregateFireTrail => ({
    ...rest,
    bfmcId: bfmc_id,
    planId: plan_id,
    length: length,
    fireTrailId: fire_trail_id,
    fireTrails: fire_trails.map(mapAPIToFireTrail),
});

export const mapAPIToFireTrailAudit = ({
    plan_id,
    change_type,
    changed_on,
    feature_global_id,
    reason_for_change,
    ...rest
}: API.FireTrailAuditRecord): FireTrailAuditRecord => ({
    ...rest,
    planId: plan_id,
    changeType: change_type,
    changedOn: new Date(changed_on),
    featureGlobalId: feature_global_id,
    reasonForChange: reason_for_change,
});

export const addFireTrail = makeAPICall<
    ActionResponse,
    FireTrailAdd,
    ActionResponse
>(
    ({ payload }) => {
        return {
            url: `${FIRE_TRAIL_ROUTE}/`,
            contentType: 'application/json',
            requestData: {
                method: 'POST',
                body: JSON.stringify({
                    featureCollection: payload,
                }),
            },
        };
    },
    (x) => x,
);

export const updateFireTrail = makeAPICall<
    ActionResponse,
    FireTrailUpdate,
    ActionResponse
>(
    ({ id, payload, reason_for_change }) => {
        return {
            url: `${FIRE_TRAIL_ROUTE}/${id}`,
            contentType: 'application/json',
            requestData: {
                method: 'PUT',
                body: JSON.stringify({
                    featureCollection: payload,
                    reason_for_change,
                }),
            },
        };
    },
    (x) => x,
);

export const bulkEditFireTrails = makeAPICall<
    ActionResponse,
    FireTrailBulkUpdate,
    ActionResponse
>(
    (payload) => {
        const formData = new FormData();
        const {
            planId,
            fireTrailId,
            tenure,
            status,
            classification,
            vehicleCarryingCapacity,
            designated,
            reason_for_change: reasonForChange,
        } = payload;
        if (planId) formData.append('plan_id', planId);
        if (fireTrailId) formData.append('fire_trail_id', fireTrailId);
        if (tenure) formData.append('tenure', tenure);
        if (status) formData.append('status', status);
        if (classification) formData.append('classification', classification);
        if (vehicleCarryingCapacity)
            formData.append(
                'vehicle_carrying_capacity',
                vehicleCarryingCapacity,
            );
        if (designated) formData.append('designated', designated);

        formData.append('reason_for_change', reasonForChange ?? '');

        return {
            url: `${FIRE_TRAIL_ROUTE}/bulk`,
            contentType: null,
            requestData: {
                method: 'PUT',
                body: formData,
            },
        };
    },
    (data) => {
        return data;
    },
);

export const bulkDeleteFireTrails = makeAPICall<
    ActionResponse,
    { fireTrailId: string; planId: string; reason_for_change: string },
    ActionResponse
>(
    (payload) => {
        if (!payload.fireTrailId || !payload.planId) {
            throw Error('No fire trail id found.');
        }
        return {
            url: `${FIRE_TRAIL_ROUTE}/${payload.planId}/bulk/${payload.fireTrailId}`,
            contentType: 'application/json',
            requestData: {
                method: 'DELETE',
                body: JSON.stringify({
                    reason_for_change: payload.reason_for_change,
                }),
            },
        };
    },
    (data) => {
        return data;
    },
);

export const deleteFireTrail = makeAPICall<
    ActionResponse,
    { id: number; reason_for_change: string },
    ActionResponse
>(
    ({ id, reason_for_change }) => ({
        url: `${FIRE_TRAIL_ROUTE}/${id}`,
        contentType: 'application/json',
        requestData: {
            method: 'DELETE',
            body: JSON.stringify({ reason_for_change }),
        },
    }),
    (x) => x,
);

export const getFireTrailList = makeAPICall<
    FireTrail[],
    FireTrailLineFilters,
    API.FireTrail[]
>(
    ({ skip, limit, planId }) => {
        const params = new URLSearchParams();
        if (skip != null) params.append('skip', `${skip}`);
        if (limit != null) params.append('limit', `${limit}`);
        if (planId != null) params.append('plan_id', `${planId}`);
        return {
            url: `${FIRE_TRAIL_ROUTE}/?${params.toString()}`,
        };
    },
    (raw) => raw.map((fmr) => mapAPIToFireTrail(fmr)),
);

export const getFireTrailAudit = makeAPICall<
    FireTrailAuditRecord[],
    FireTrailAuditFields,
    API.FireTrailAuditRecord[]
>(
    ({ planId, globalFeatureID }) => {
        return {
            url: `${FIRE_TRAIL_ROUTE}/audit/${planId}/${globalFeatureID}`,
        };
    },
    (raw) => raw.map((record) => mapAPIToFireTrailAudit(record)),
);

export const getAggregateFireTrails = makeAPICall<
    AggregateFireTrail[],
    FireTrailLineFilters,
    API.AggregateFireTrail[]
>(
    ({ skip, limit, planId }) => {
        const params = new URLSearchParams();
        if (skip != null) params.append('skip', `${skip}`);
        if (limit != null) params.append('limit', `${limit}`);
        if (planId != null) params.append('plan_id', `${planId}`);
        return {
            url: `${FIRE_TRAIL_ROUTE}/aggregate/?${params.toString()}`,
        };
    },
    (raw) => raw.map((fmr) => mapAPIToAggregateFireTrail(fmr)),
);

export const getFireTrailSuggestions = makeAPICall<
    string[],
    { fire_trail_id: string },
    string[]
>(
    ({ fire_trail_id }) => {
        const params = new URLSearchParams();
        if (fire_trail_id != null)
            params.append('fire_trail_id', `${fire_trail_id}`);
        return {
            url: `${FIRE_TRAIL_ROUTE}/suggestions/extended/?${params.toString()}`,
        };
    },
    (raw) => {
        return raw;
    },
);

export const getSelectedFeatureFromGeoserver = makeAPICall<
    Feature<Geometry>,
    FireTrailLineFilters,
    any
>(
    ({ featureID }) => {
        const params = new URLSearchParams({
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeName: 'risk-planning:FaftLine',
            outputFormat: 'application/json',
            srsname: DATA_PROJECTION.getCode(),
            featureID: `${featureID}`,
        });
        return {
            url: `${GEOSERVER_URL}/wfs?${params.toString()}`,
        };
    },
    (raw) => {
        const feature = new olGeoJson({
            dataProjection: DATA_PROJECTION,
            featureProjection: MAP_PROJECTION,
        }).readFeatures(raw)?.[0];
        return feature;
    },
);

export const importDataToFireTrail = makeAPICall<
    ActionResponse,
    FireTrailDataImport,
    ActionResponse
>(
    (payload) => {
        return {
            url: `${FIRE_TRAIL_ROUTE}/import-data/extended`,
            contentType: 'application/json',
            requestData: {
                method: 'POST',
                body: JSON.stringify(payload),
            },
        };
    },
    (data) => {
        return data;
    },
);

export const uploadFireTrail = makeAPICall<
    ActionResponse,
    FireTrailUpload,
    ActionResponse
>(
    ({ bfmcId, planId, uploadType, dataFile, reasonForChange }) => {
        const formData = new FormData();
        formData.append('data_file', dataFile);
        formData.append('upload_type', uploadType);
        formData.append('reason_for_change', reasonForChange);
        return {
            url: `${FIRE_TRAIL_ROUTE}/data-upload/${bfmcId}/${planId}`,
            contentType: 'auto',
            requestData: {
                method: 'POST',
                body: formData,
            },
        };
    },
    (data) => {
        return data;
    },
);

export interface DataUpload {
    id: number;
    uploadId: string;
    uploadType: string;
    filename: string;
    status: string;
    errorMessage: string;
    uploadedByUser: string;
}

export const mapAPIToFireTrailUpload = ({
    upload_id,
    upload_type,
    error_msg,
    uploaded_by_user,
    ...rest
}: API.DataUpload): DataUpload => ({
    ...rest,
    uploadId: upload_id,
    uploadType: upload_type,
    errorMessage: error_msg,
    uploadedByUser: uploaded_by_user,
});

export const getFireTrailUploads = makeAPICall<
    DataUpload[],
    GetFireTrailUploads,
    API.DataUpload[]
>(
    ({ bfmcId, planId }) => {
        return {
            url: `${FIRE_TRAIL_ROUTE}/data-upload/${bfmcId}/${planId}?limit=3`,
        };
    },
    (raw) => raw.map(mapAPIToFireTrailUpload),
);

export const getFireTrailExtent = makeAPICall<
    Extent,
    { fireTrailId: string },
    API.Extent
>(
    ({ fireTrailId }) => {
        return {
            url: `${FIRE_TRAIL_ROUTE}/extent/${fireTrailId}`,
        };
    },
    (raw) => {
        return raw;
    },
);

export const fireTrailsClassifications = [
    'Unknown',
    'Strategic',
    'Tactical',
    'Track (Other)',
    'Road',
];

export const fireTrailsHighMediumLow = ['High', 'Medium', 'Low', ''];

export const isValidFireTrailClassification = (
    classification: string | undefined | null,
    required = true,
): boolean => {
    if (!classification) return !required;
    return fireTrailsClassifications.includes(classification);
};

export const fireTrailsVehicleCarryingCapacity = [
    'Unknown',
    'Category 1',
    'Category 7',
    'Category 9',
];

export const isValidFireTrailsVehicleCarryingCapacity = (
    VCC: string | undefined | null,
    required = true,
): boolean => {
    if (!VCC) return !required;
    return fireTrailsVehicleCarryingCapacity.includes(VCC);
};

export const fireTrailsWorkType = ['New', 'Upgrade', 'Remove', 'Maintain'];

// export const isValidFireTrailsWorksType = (
//     worksType: string | undefined | null,
// ): boolean => {
//     if (!worksType) return true;
//     return fireTrailsWorkType.includes(worksType);
// };

export const isValidRelevance = (
    relevance: number | undefined | null,
): boolean => {
    if (relevance === undefined || relevance === null) return true;
    if (0 <= relevance && relevance <= 10) return true;
    return false;
};

export enum FireTrailSources {
    BFRP = 'Drawn in BFRP',
    FIRE_TRAIL_LAYER = 'Fire Trail Layer',
    IMPORTED = 'Imported',
}

export const fireTrailsSources = Object.values(FireTrailSources);

export const isValidSource = (source: string | undefined | null): boolean => {
    if (!source) return true;
    return (fireTrailsSources as string[]).includes(source);
};

export const fireTrailsLeadAgencies = [
    'SA Country Fire Service',
    'SA Metropolitan Fire Service',
    'Department for Environment and Water',
    'ForestrySA',
    'Private',
];

export const isValidLeadAgencies = (
    _agencies: string | undefined | null,
): boolean => {
    return true;
};

export const fireTrailsStatus = ['Registered', 'Existing', 'New'];

export const isValidFireTrailStatus = (status?: string): boolean => {
    if (!status) return true;
    return (fireTrailsStatus as string[]).includes(status);
};

export const fireTrailsTenure = [
    'Single Tenure - Private',
    'Single Tenure - Public',
    'Multi-Tenure',
];

export const isValidFireTrailTenure = (tenure?: string): boolean => {
    if (!tenure) return true;
    return (fireTrailsTenure as string[]).includes(tenure);
};

const validationRegExpression = {
    fireTrailId: /^\d{3}[A-Z]{3,8}_\d{1,5}$/,
    fireTrailSegmentId: /^\d{3}[A-Z]{3,8}_\d{1,5}$/,
};

export const isValidFireTrailId = (fireTrailId?: string): boolean => {
    return validationRegExpression.fireTrailId.test(fireTrailId || '');
};

export const isValidFireTrailSegmentId = (
    fireTrailSegmentId?: string,
): boolean => {
    return validationRegExpression.fireTrailSegmentId.test(
        fireTrailSegmentId || '',
    );
};

export const isValidSourceFireTrailId = (fireTrailId?: string): boolean => {
    return validationRegExpression.fireTrailId.test(fireTrailId || '');
};

export const isValidSourceFireTrailSegmentId = (
    fireTrailSegmentId?: string,
): boolean => {
    return validationRegExpression.fireTrailSegmentId.test(
        fireTrailSegmentId || '',
    );
};
